import { BaseApiClient } from "@/lib/api/baseApiClient.service";
import type { KorrekturStudentKlausurWithKorrekturStepDTO } from "./korrektur.types";

/**
 * Service for handling Korrektur (correction) related API calls
 */
class KorrekturService {
	private static instance: KorrekturService;
	private baseApi: BaseApiClient;

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): KorrekturService {
		if (!KorrekturService.instance) {
			KorrekturService.instance = new KorrekturService();
		}
		return KorrekturService.instance;
	}

	/**
	 * Gets the korrekturUserKlausur data for a userKlausur and also gets the korrekturStep data for the current klausur
	 * @param korrekturStepId - The id of the korrektur step
	 * @returns The korrekturUserKlausur data
	 */
	public async getKorrekturUserKlausurData(
		korrekturStepId: string,
	): Promise<KorrekturStudentKlausurWithKorrekturStepDTO> {
		if (!korrekturStepId) {
			return Promise.reject(new Error("Korrektur Step ID is required"));
		}

		const evalData: KorrekturStudentKlausurWithKorrekturStepDTO =
			await this.baseApi.get<KorrekturStudentKlausurWithKorrekturStepDTO>(
				`korrektur/${korrekturStepId}/get-korrektur-by-korrektur-step`,
			);

		return evalData;
	}

	/**
	 * Updates the points for a korrekturbogen item
	 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
	 * @param korrekturbogenItemId - The id of the korrekturbogen item
	 * @param userPoints - The points to update
	 * @returns Promise with the boolean result
	 */
	public async updateKorrekturbogenItemPoints(
		korrekturStudentKlausurId: string,
		korrekturbogenItemId: string,
		userPoints: number,
	): Promise<boolean> {
		try {
			await this.baseApi.put(
				`korrektur/${korrekturStudentKlausurId}/korrektur-korrekturbogen-item/${korrekturbogenItemId}/update-user-points`,
				{
					userPoints,
				},
			);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Updates the points for a musterloesung
	 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
	 * @param korrekturMusterloesungId - The id of the korrektur musterloesung
	 * @param userPoints - The points to update
	 * @returns Promise with the boolean result
	 */
	public async updateMusterloesungPoints(
		korrekturStudentKlausurId: string,
		korrekturMusterloesungId: string,
		userPoints: number,
	): Promise<boolean> {
		try {
			await this.baseApi.put(
				`korrektur/${korrekturStudentKlausurId}/korrektur-musterloesung/${korrekturMusterloesungId}/update-user-points`,
				{
					userPoints,
				},
			);
			return true;
		} catch {
			return false;
		}
	}
	/**
	 * Fetches the PDF file as a blob with proper authentication
	 * @param korrekturStudentKlausurId - The korrektur ID
	 * @returns Promise with the PDF blob data
	 */
	public async getPdfBlob(korrekturStudentKlausurId: string): Promise<Blob> {
		if (!korrekturStudentKlausurId) {
			throw new Error("Korrektur Student Klausur ID is required");
		}

		// Use the baseApi client which handles authentication headers automatically
		return await this.baseApi.getBlob(
			`korrektur/${korrekturStudentKlausurId}/pdf`,
		);
	}

	/**
	 * Fetches the finished korrektur PDF as a blob
	 * @param korrekturStepId - The korrektur step ID
	 * @returns Promise with the PDF blob data
	 */
	public async getFinishedKorrekturPdfBlob(
		korrekturStepId: string,
	): Promise<Blob> {
		if (!korrekturStepId) {
			throw new Error("Korrektur Step ID is required");
		}

		return await this.baseApi.getBlob(
			`korrektur/${korrekturStepId}/get-finished-korrektur-pdf`,
		);
	}

	/**
	 * Submits the korrektur (correction) for final review
	 * @param korrekturStepId - The korrektur ID to submit
	 * @returns Promise that resolves when the korrektur is submitted
	 */
	public async submitKorrektur(korrekturStepId: string): Promise<void> {
		if (!korrekturStepId) {
			throw new Error("Korrektur Student Klausur ID is required");
		}

		await this.baseApi.post(`korrektur/${korrekturStepId}/submit`, {});
	}
}

export default KorrekturService;
